import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Loader2 } from "lucide-react";

import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { Input, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { cn } from "@/lib/utils";
import { formSnapshotDirty } from "@/lib/formDirty";
import {
  pdfTemplateDocumentTypeLabel,
  templateDetailTitle,
  templateFamilyOlderRows,
  templateFamilyVisibleRows,
  templateGroupTitle,
  templateVersionLabel,
  type PdfTemplateListRow,
} from "./pdfTemplatesListUi";

const PDF_DOCUMENT_TYPES = [
  "transfer-document",
  "boxes-pdf",
  "invoice-pdf",
  "send-invoice",
] as const;

const DEFAULT_TRANSFER_DOCUMENT_SAMPLE = {
  transferNumber: "T-0045",
  destination: {
    recipient: "Smart Grid Solutions",
    address1: "2400 Grand Ave Pwky",
    address2: "Suite 103",
    city: "Austin",
    state: "TX",
    zip: "78728",
  },
  shipByDate: "2026-06-22",
  reason: "Transfer",
  notes: "Non-SGS origin — shipper packs cartons off-system.",
  boxes: [
    {
      boxNumber: 1,
      items: [
        {
          transferLine: 1,
          boxLine: 1,
          sku: "FI-3C",
          description: "Widget",
          quantity: 100,
        },
        {
          transferLine: 2,
          boxLine: 2,
          sku: "90-002",
          description: "90-002",
          quantity: 40,
        },
      ],
    },
  ],
  generatedAt: new Date().toISOString(),
};

interface TemplateDraft {
  template_family: string;
  document_type: string;
  name: string;
  notes: string;
  template: string;
  sample_data_text: string;
}

const EMPTY_DRAFT: TemplateDraft = {
  template_family: "",
  document_type: "transfer-document",
  name: "",
  notes: "",
  template: "{}\n",
  sample_data_text: JSON.stringify(DEFAULT_TRANSFER_DOCUMENT_SAMPLE, null, 2),
};

interface PreviewResponse {
  pdf_base64: string;
  sample_data: Record<string, unknown>;
  request_body: unknown;
}

function draftFromRow(row: PdfTemplateListRow): TemplateDraft {
  return {
    template_family: row.template_family,
    document_type: row.document_type,
    name: row.name,
    notes: row.notes ?? "",
    template: row.template,
    sample_data_text:
      row.sample_data != null ? JSON.stringify(row.sample_data, null, 2) : EMPTY_DRAFT.sample_data_text,
  };
}

function parseSampleDataText(text: string): Record<string, unknown> | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const parsed: unknown = JSON.parse(trimmed);
  if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Sample data must be a JSON object.");
  }
  return parsed as Record<string, unknown>;
}

function previewPdfUrl(pdfBase64: string): string {
  return `data:application/pdf;base64,${pdfBase64}`;
}

export function PdfTemplatesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();

  const templatesQuery = useQuery({
    queryKey: ["admin", "pdf-templates"],
    queryFn: () => apiFetch<{ rows: PdfTemplateListRow[] }>("/api/admin/pdf-templates"),
  });

  const rows = useMemo(() => templatesQuery.data?.rows ?? [], [templatesQuery.data]);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<TemplateDraft | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [expandedFamilies, setExpandedFamilies] = useState<Record<string, boolean>>({});

  const selectedRow = rows.find((row) => row.id === selectedId) ?? null;
  const activeDraft = draft ?? (selectedRow != null ? draftFromRow(selectedRow) : null);
  const templateDirty =
    !creating &&
    selectedRow != null &&
    activeDraft != null &&
    formSnapshotDirty(activeDraft, draftFromRow(selectedRow));

  function selectRow(row: PdfTemplateListRow): void {
    setSelectedId(row.id);
    setCreating(false);
    setDraft(draftFromRow(row));
    setPreview(null);
    if (!row.is_active) {
      setExpandedFamilies((current) => ({ ...current, [row.template_family]: true }));
    }
  }

  function toggleFamilyExpanded(family: string): void {
    setExpandedFamilies((current) => ({ ...current, [family]: !current[family] }));
  }

  function startNewTemplate(): void {
    setSelectedId(null);
    setCreating(true);
    setDraft({ ...EMPTY_DRAFT });
    setPreview(null);
  }

  function updateDraft(patch: Partial<TemplateDraft>): void {
    setDraft((current) => ({ ...(current ?? activeDraft ?? EMPTY_DRAFT), ...patch }));
  }

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "pdf-templates"] });
  };

  function templateBody(input: TemplateDraft) {
    let sampleData: Record<string, unknown> | null = null;
    if (input.sample_data_text.trim() !== "") {
      sampleData = parseSampleDataText(input.sample_data_text);
    }
    return {
      template_family: input.template_family.trim(),
      document_type: input.document_type.trim(),
      name: input.name.trim(),
      notes: input.notes.trim() === "" ? null : input.notes.trim(),
      template: input.template,
      sample_data: sampleData,
    };
  }

  const saveMutation = useMutation({
    mutationFn: (input: { id: number; draft: TemplateDraft }) =>
      apiFetch(`/api/admin/pdf-templates/${String(input.id)}`, {
        method: "PUT",
        body: templateBody(input.draft),
      }),
    onSuccess: () => {
      invalidate();
      showToast({ tone: "success", message: "Template saved." });
    },
    onError: (error) => showToast({ tone: "error", message: (error as Error).message }),
  });

  const createMutation = useMutation({
    mutationFn: (input: TemplateDraft) =>
      apiFetch<{ id: number }>("/api/admin/pdf-templates", {
        method: "POST",
        body: templateBody(input),
      }),
    onSuccess: (data) => {
      invalidate();
      setCreating(false);
      setSelectedId(data.id);
      showToast({ tone: "success", message: "Template created." });
    },
    onError: (error) => showToast({ tone: "error", message: (error as Error).message }),
  });

  const activateMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/admin/pdf-templates/${String(id)}/activate`, { method: "POST" }),
    onSuccess: () => {
      invalidate();
      showToast({ tone: "success", message: "Template activated." });
    },
    onError: (error) => showToast({ tone: "error", message: (error as Error).message }),
  });

  const previewMutation = useMutation({
    mutationFn: (input: TemplateDraft) => {
      const sampleData =
        input.sample_data_text.trim() === "" ? null : parseSampleDataText(input.sample_data_text);
      return apiFetch<PreviewResponse>("/api/admin/pdf-templates/render-preview", {
        method: "POST",
        body: {
          template: input.template,
          document_type: input.document_type.trim(),
          template_family: input.template_family.trim(),
          sample_data: sampleData,
        },
      });
    },
    onSuccess: (data) => setPreview(data),
    onError: (error) => {
      setPreview(null);
      showToast({ tone: "error", message: (error as Error).message });
    },
  });

  const familyGroups = useMemo(() => {
    const byFamily = new Map<string, PdfTemplateListRow[]>();
    for (const row of rows) {
      const list = byFamily.get(row.template_family) ?? [];
      list.push(row);
      byFamily.set(row.template_family, list);
    }
    return [...byFamily.entries()].sort(([left], [right]) => left.localeCompare(right));
  }, [rows]);

  const detailTitle =
    creating ? "New template" : selectedRow != null ? templateDetailTitle(selectedRow) : "Template";

  const previewUrl = preview?.pdf_base64 != null ? previewPdfUrl(preview.pdf_base64) : null;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <PageTitleWithHelp hoverHint="Versioned pdf-it document templates stored in public.pdf_templates. Create families, save new versions, activate one version per family and document type, and preview PDF output through pdf-it. Production document paths still use hardcoded builders until a later cutover wires pdf-it to the active row here.">
          PDF templates
        </PageTitleWithHelp>
        <Button type="button" size="sm" onClick={startNewTemplate}>
          New template
        </Button>
      </header>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <Card className="self-start">
          <CardHeader>
            <CardTitle>{`Templates (${String(rows.length)})`}</CardTitle>
          </CardHeader>
          <CardBody className="max-h-[70vh] space-y-3 overflow-y-auto">
            {templatesQuery.isLoading ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : templatesQuery.isError ? (
              <p className="text-sm text-rose-700">{(templatesQuery.error as Error).message}</p>
            ) : familyGroups.length === 0 ? (
              <p className="text-sm text-slate-500">No templates yet.</p>
            ) : (
              familyGroups.map(([family, familyRows]) => {
                const olderRows = templateFamilyOlderRows(familyRows);
                const hasOlderVersions = olderRows.length > 0;
                const expanded = expandedFamilies[family] === true;
                const visibleRows = templateFamilyVisibleRows(familyRows, expanded);

                return (
                  <div key={family}>
                    {hasOlderVersions ? (
                      <button
                        type="button"
                        aria-expanded={expanded}
                        onClick={() => toggleFamilyExpanded(family)}
                        className="flex w-full items-center gap-1 rounded-md py-0.5 text-left text-xs font-semibold tracking-wide text-slate-600 hover:bg-slate-50"
                      >
                        <ChevronRight
                          aria-hidden
                          className={cn(
                            "h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform",
                            expanded ? "rotate-90" : "",
                          )}
                          strokeWidth={2}
                        />
                        <span className="min-w-0 flex-1 truncate">{templateGroupTitle(familyRows)}</span>
                        {!expanded ? (
                          <span className="shrink-0 text-[11px] font-normal text-slate-400">
                            {`${String(olderRows.length)} older`}
                          </span>
                        ) : null}
                      </button>
                    ) : (
                      <p className="px-1 text-xs font-semibold tracking-wide text-slate-600">
                        {templateGroupTitle(familyRows)}
                      </p>
                    )}
                    <ul className="mt-1 space-y-1">
                      {visibleRows.map((row) => (
                        <li key={row.id}>
                          <button
                            type="button"
                            onClick={() => selectRow(row)}
                            className={cn(
                              "flex w-full items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-left text-sm",
                              row.id === selectedId
                                ? "border-brand-500 bg-brand-50 text-slate-900"
                                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                            )}
                          >
                            <span className="min-w-0 font-medium tabular-nums">{templateVersionLabel(row)}</span>
                            <span className="flex shrink-0 items-center gap-1">
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                                {pdfTemplateDocumentTypeLabel(row.document_type)}
                              </span>
                              {row.is_active ? (
                                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
                                  Active
                                </span>
                              ) : null}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })
            )}
          </CardBody>
        </Card>

        <div className="min-w-0 space-y-4">
          {activeDraft == null ? (
            <Card>
              <CardBody>
                <p className="text-sm text-slate-500">
                  Select a template on the left, or create a new one to begin.
                </p>
              </CardBody>
            </Card>
          ) : (
            <Card>
              <CardBody className="space-y-5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <h2 className="text-lg font-semibold text-slate-900">{detailTitle}</h2>
                  {selectedRow != null && !creating ? (
                    <span className="text-sm font-medium tabular-nums text-slate-500">
                      {templateVersionLabel(selectedRow)}
                    </span>
                  ) : null}
                  {selectedRow?.is_active ? (
                    <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                      Active
                    </span>
                  ) : null}
                </div>

                <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(280px,26rem)]">
                  <div className="flex min-h-[24rem] flex-col gap-4">
                    <div className="flex min-h-0 flex-1 flex-col">
                      <Label htmlFor="pdf-template-sample-data">Sample data (JSON object)</Label>
                      <textarea
                        id="pdf-template-sample-data"
                        value={activeDraft.sample_data_text}
                        onChange={(event) => updateDraft({ sample_data_text: event.target.value })}
                        spellCheck={false}
                        className="mt-1 min-h-[10rem] flex-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </div>
                    <div className="flex min-h-0 flex-1 flex-col">
                      <Label htmlFor="pdf-template-body">Template (Handlebars JSON)</Label>
                      <textarea
                        id="pdf-template-body"
                        value={activeDraft.template}
                        onChange={(event) => updateDraft({ template: event.target.value })}
                        spellCheck={false}
                        placeholder={'Use {} to pass sample data through unchanged.'}
                        className="mt-1 min-h-[10rem] flex-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </div>
                  </div>
                  <div className="flex min-h-[24rem] flex-col">
                    <p className="text-xs leading-5 text-slate-400">
                      Preview calls pdf-it with rendered sample data. Production paths do not read this table yet.
                    </p>
                    <div
                      aria-live="polite"
                      className="mt-1 flex min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white"
                    >
                      {previewMutation.isPending ? (
                        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-slate-500">
                          <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
                          Rendering preview…
                        </div>
                      ) : previewUrl != null ? (
                        <iframe
                          title="PDF template preview"
                          src={previewUrl}
                          className="h-full min-h-[24rem] w-full"
                        />
                      ) : (
                        <p className="m-auto max-w-xs px-4 text-center text-sm text-slate-500">
                          Preview PDF to render the document here.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    isLoading={previewMutation.isPending}
                    onClick={() => {
                      try {
                        previewMutation.mutate(activeDraft);
                      } catch (error) {
                        showToast({ tone: "error", message: (error as Error).message });
                      }
                    }}
                  >
                    Preview PDF
                  </Button>
                </div>

                <div className="space-y-3 border-t border-slate-200 pt-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Metadata</p>
                  <div className="grid gap-3 md:grid-cols-3">
                    <div>
                      <Label htmlFor="pdf-template-family">Template family</Label>
                      <Input
                        id="pdf-template-family"
                        value={activeDraft.template_family}
                        onChange={(event) => updateDraft({ template_family: event.target.value })}
                        placeholder="e.g. transfer-order-external"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pdf-template-name">Name</Label>
                      <Input
                        id="pdf-template-name"
                        value={activeDraft.name}
                        onChange={(event) => updateDraft({ name: event.target.value })}
                        placeholder="e.g. External transfer order"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pdf-template-document-type">Document type</Label>
                      <select
                        id="pdf-template-document-type"
                        value={activeDraft.document_type}
                        onChange={(event) => updateDraft({ document_type: event.target.value })}
                        className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        {PDF_DOCUMENT_TYPES.map((documentType) => (
                          <option key={documentType} value={documentType}>
                            {pdfTemplateDocumentTypeLabel(documentType)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <Label htmlFor="pdf-template-version">Version</Label>
                      <Input
                        id="pdf-template-version"
                        value={selectedRow != null ? String(selectedRow.version) : "—"}
                        className="mt-1"
                        readOnly
                      />
                    </div>
                    <div>
                      <Label htmlFor="pdf-template-id">Template id</Label>
                      <Input
                        id="pdf-template-id"
                        value={selectedRow != null ? String(selectedRow.id) : "—"}
                        className="mt-1"
                        readOnly
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="pdf-template-notes">Notes</Label>
                    <Input
                      id="pdf-template-notes"
                      value={activeDraft.notes}
                      onChange={(event) => updateDraft({ notes: event.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-5">
                  {creating ? (
                    <Button
                      type="button"
                      size="sm"
                      isLoading={createMutation.isPending}
                      disabled={
                        activeDraft.template_family.trim() === ""
                        || activeDraft.name.trim() === ""
                        || activeDraft.template.trim() === ""
                      }
                      onClick={() => {
                        try {
                          createMutation.mutate(activeDraft);
                        } catch (error) {
                          showToast({ tone: "error", message: (error as Error).message });
                        }
                      }}
                    >
                      Create template
                    </Button>
                  ) : (
                    <>
                      <HubSaveButton
                        type="button"
                        size="sm"
                        dirty={templateDirty}
                        disabled={selectedRow == null}
                        isLoading={saveMutation.isPending}
                        onClick={() => {
                          if (selectedRow == null) return;
                          try {
                            saveMutation.mutate({ id: selectedRow.id, draft: activeDraft });
                          } catch (error) {
                            showToast({ tone: "error", message: (error as Error).message });
                          }
                        }}
                      >
                        Save changes
                      </HubSaveButton>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        isLoading={createMutation.isPending}
                        onClick={() => {
                          try {
                            createMutation.mutate(activeDraft);
                          } catch (error) {
                            showToast({ tone: "error", message: (error as Error).message });
                          }
                        }}
                      >
                        Save as new version
                      </Button>
                      {selectedRow != null && !selectedRow.is_active ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          isLoading={activateMutation.isPending}
                          onClick={() => activateMutation.mutate(selectedRow.id)}
                        >
                          Activate
                        </Button>
                      ) : null}
                    </>
                  )}
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
